import React, { useState, useEffect } from 'react';
import dataManager from '../utils/dataManager';
import useLoadMore from '../utils/useLoadMore';
import LoadMoreButton from './LoadMoreButton';
import { getAuthorDisplayName } from '../utils/userUtils';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config/appConfig';
import { getAuthHeaders } from '../utils/authApi';
import ConfigService from '../utils/configService';

const FeelingInsights = () => {
  const { user } = useAuth();
  const [selectedType, setSelectedType] = useState('all');
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [newInsight, setNewInsight] = useState({
    title: '',
    insight_type: 'emotional',
    description: '',
    is_public: true,
  });

  // 加载感受洞察数据
  useEffect(() => {
    const loadInsights = async () => {
      try {
        setLoading(true);
        setError(null);
        const insightsData = await dataManager.getFeelingInsights();
        setInsights(Array.isArray(insightsData) ? insightsData : []);
      } catch (err) {
        console.error('Error loading feeling insights:', err);
        setError('加载感受洞察失败，请确保Django服务器正在运行');
        setInsights([]);
      } finally {
        setLoading(false);
      }
    };

    loadInsights();
  }, []);

  // 从数据管理器中获取数据
  const insightTypes = dataManager.getInsightTypes() || [];

  const getErrorMessage = (data, fallback) => {
    if (!data || typeof data !== 'object') return fallback;
    if (data.detail) return data.detail;
    if (data.non_field_errors?.[0]) return data.non_field_errors[0];

    const firstKey = Object.keys(data)[0];
    const firstValue = firstKey ? data[firstKey] : null;
    if (Array.isArray(firstValue) && firstValue[0]) {
      return `${firstKey}: ${firstValue[0]}`;
    }
    return fallback;
  };

  const handleAddInsight = async (event) => {
    event.preventDefault();

    if (!user) {
      alert('请先登录才能分享洞察');
      return;
    }
    if (!newInsight.title.trim() || !newInsight.description.trim()) {
      alert('请填写标题和洞察内容');
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch(ConfigService.getApiUrl(API_ENDPOINTS.INSIGHTS), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title: newInsight.title.trim(),
          insight_type: newInsight.insight_type,
          description: newInsight.description.trim(),
          is_public: newInsight.is_public,
          related_pose_ids: [],
        }),
      });
      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(getErrorMessage(responseData, '分享洞察失败'));
      }

      dataManager.invalidateCache('feelingInsights');
      if (responseData.is_public) {
        setInsights(prev => [responseData, ...prev]);
      }
      setNewInsight({ title: '', insight_type: 'emotional', description: '', is_public: true });
      setShowAddForm(false);
      alert('洞察已分享到社区！');
    } catch (err) {
      console.error('Error saving insight:', err);
      alert(err.message || '分享洞察失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };



  const filteredInsights = selectedType === 'all'
    ? insights
    : insights.filter(insight => insight.type === selectedType);

  // 渐进式分页：首批 10 条，"加载更多"追加，切换类型时自动重置
  const {
    visibleItems: visibleInsights,
    hasMore: hasMoreInsights,
    remaining: remainingInsights,
    loadMore: loadMoreInsights,
  } = useLoadMore(filteredInsights, 10, selectedType);

  const verifiedInsights = insights.filter(insight => insight.verified);

  // 加载状态
  if (loading) {
    return (
      <div className="max-w-6xl mx-auto flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <span className="text-white text-2xl">💭</span>
          </div>
          <p className="text-purple-600">正在加载感受洞察...</p>
        </div>
      </div>
    );
  }

  // 错误状态
  if (error) {
    return (
      <div className="max-w-6xl mx-auto flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-500 text-2xl">⚠️</span>
          </div>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
          >
            重新加载
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-emerald-800 mb-4">感受洞察</h1>
        <p className="text-lg text-emerald-600 max-w-2xl mx-auto">
          深入探索瑜伽练习中的身心体验，分享和学习珍贵的练习洞察
        </p>
      </div>

      {/* Verified Insights */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-3xl p-8">
        <h2 className="text-2xl font-bold text-emerald-800 mb-6 flex items-center">
          <span className="mr-3 text-3xl">✅</span>
          验证洞察
          <span className="ml-2 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm">
            专业认证
          </span>
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          {verifiedInsights.slice(0, 4).map((insight) => (
            <div key={insight.id} className="bg-white/70 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-semibold text-emerald-800 flex-1">{insight.title}</h3>
                <span className="text-2xl ml-2">
                  {insightTypes.find(type => type.id === insight.type)?.emoji}
                </span>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed mb-4 line-clamp-3">
                {insight.description}
              </p>
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-4">
                  <span className="text-emerald-600 font-medium">{getAuthorDisplayName(insight.author)}</span>
                  <span className="text-gray-500">{insight.date}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-blue-500">👍</span>
                  <span className="text-gray-600">{insight.helpful}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Type Filter */}
      <div className="bg-white/70 rounded-2xl p-6">
        <h3 className="text-xl font-semibold text-emerald-800 mb-4">洞察类型</h3>
        <div className="flex flex-wrap gap-3">
          {insightTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => setSelectedType(type.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                selectedType === type.id
                  ? 'bg-emerald-500 text-white shadow-lg'
                  : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
              }`}
            >
              <span className="mr-2">{type.emoji}</span>
              {type.name}
            </button>
          ))}
        </div>
      </div>

      {/* Insights List */}
      <div className="space-y-6">
        <h3 className="text-2xl font-bold text-emerald-800">
          {selectedType === 'all' ? '所有洞察' : insightTypes.find(type => type.id === selectedType)?.name}
          <span className="text-lg font-normal text-emerald-600 ml-2">
            ({filteredInsights.length} 个洞察)
          </span>
        </h3>
        
        <div className="grid gap-6">
          {visibleInsights.map((insight) => (
            <div key={insight.id} className="bg-white/70 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h4 className="text-xl font-semibold text-emerald-800 mb-2">{insight.title}</h4>
                  <div className="flex items-center space-x-4 text-sm text-emerald-600">
                    <span className="flex items-center">
                      <span className="mr-1">
                        {insightTypes.find(type => type.id === insight.type)?.emoji}
                      </span>
                      {insightTypes.find(type => type.id === insight.type)?.name}
                    </span>
                    <span>by {getAuthorDisplayName(insight.author)}</span>
                    <span>{insight.date}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {insight.verified && (
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                      已验证
                    </span>
                  )}
                </div>
              </div>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                {insight.description}
              </p>
              
              <div className="flex items-center justify-between">
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm text-emerald-600 font-medium">相关体式：</span>
                  {Array.isArray(insight.relatedPoses) ? insight.relatedPoses.map((pose, index) => (
                    <span key={index} className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full text-xs">
                      {pose}
                    </span>
                  )) : null}
                </div>
                <div className="flex items-center space-x-4">
                  <button className="flex items-center space-x-1 text-gray-600 hover:text-blue-500 transition-colors">
                    <span>👍</span>
                    <span>{insight.helpful}</span>
                  </button>
                  <button className="text-emerald-600 hover:text-emerald-700 transition-colors">
                    分享
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <LoadMoreButton hasMore={hasMoreInsights} remaining={remainingInsights} onClick={loadMoreInsights} />
      </div>

      {/* Add Insight Button */}
      <div className="text-center">
        <button
          type="button"
          onClick={() => {
            if (!user) {
              alert('请先登录才能分享洞察');
              return;
            }
            setShowAddForm(prev => !prev);
          }}
          className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-8 py-3 rounded-full font-medium hover:from-emerald-600 hover:to-teal-700 transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          {showAddForm ? '取消分享' : '分享我的洞察'}
        </button>
      </div>

      {showAddForm && user && (
        <form onSubmit={handleAddInsight} className="bg-white/70 rounded-2xl p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-emerald-800 mb-2">洞察标题</label>
            <input
              type="text"
              value={newInsight.title}
              onChange={(event) => setNewInsight(prev => ({ ...prev, title: event.target.value }))}
              className="w-full px-4 py-3 border border-emerald-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="例如：呼吸让我在挑战中稳定下来"
              maxLength="200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-emerald-800 mb-2">洞察类型</label>
            <select
              value={newInsight.insight_type}
              onChange={(event) => setNewInsight(prev => ({ ...prev, insight_type: event.target.value }))}
              className="w-full px-4 py-3 border border-emerald-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              {insightTypes.filter(type => type.id !== 'all').map(type => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-emerald-800 mb-2">洞察内容</label>
            <textarea
              value={newInsight.description}
              onChange={(event) => setNewInsight(prev => ({ ...prev, description: event.target.value }))}
              rows={5}
              className="w-full px-4 py-3 border border-emerald-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
              placeholder="写下你的练习发现、身体感受或心理变化..."
            />
          </div>
          <label className="flex items-start gap-3 rounded-lg border border-emerald-100 bg-emerald-50/50 p-4 text-sm text-emerald-800">
            <input
              type="checkbox"
              checked={newInsight.is_public}
              onChange={(event) => setNewInsight(prev => ({ ...prev, is_public: event.target.checked }))}
              className="mt-1 h-4 w-4 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span>
              <span className="font-semibold">公开给其他人查看</span>
              <span className="block text-emerald-600">关闭后只会出现在“我的帖子”里。</span>
            </span>
          </label>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowAddForm(false)}
              className="px-5 py-2 border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50 transition-colors"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={submitting || !newInsight.title.trim() || !newInsight.description.trim()}
              className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? '分享中...' : '分享到社区'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default FeelingInsights;
